import type { ColorToken, TaskDTO } from '@hearth/shared';
import { clsx } from 'clsx';
import { type PointerEvent as ReactPointerEvent, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { calendarBlockClasses } from '../../lib/colors';
import { DEFAULT_LOCALE } from '../../lib/format';
import { EntryIcons, OverdueDot, entryFillClass } from './EntryParts';
import {
  type CalendarEntry,
  type DaySegment,
  type DraftSlot,
  HOUR_HEIGHT,
  MINUTES_PER_DAY,
  MIN_DRAWN_MINUTES,
  type Reschedule,
  allDaySlot,
  changesSchedule,
  dateAtMinutes,
  dayDiff,
  daySegments,
  entryLabel,
  entryTiming,
  formatCompactTime,
  formatTime,
  formatTimeRange,
  isOverdue,
  isSameDay,
  layoutMonthWeek,
  layoutTimedEntries,
  moveByDays,
  moveTimed,
  rangeSlot,
  resizeTimed,
  slotAt,
  snapMinutes,
  wallMinutes,
} from './model';
import { type Point, usePointerGesture } from './usePointerGesture';

const DAY_HEIGHT = 24 * HOUR_HEIGHT;
const GUTTER = '3.5rem';
const LANE_HEIGHT = 22;
const MIN_BLOCK_PX = (MIN_DRAWN_MINUTES / 60) * HOUR_HEIGHT;
/** Blocks at least this tall show the time on a second line. */
const TWO_LINE_PX = 34;
const EDGE_SCROLL_PX = 28;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const WEEKDAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'short' });
const FULL_DAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'long', month: 'long', day: 'numeric' });

type GridDrag =
  | { kind: 'select'; col: number; from: number; to: number }
  | { kind: 'lane-select'; from: number; to: number }
  | { kind: 'move' | 'resize' | 'lane-move'; task: TaskDTO; change: Reschedule | null };

export type TimeGridViewProps = {
  /** Columns: one day, or the week (weekends possibly hidden). */
  days: Date[];
  entries: CalendarEntry[];
  now: Date;
  canEdit: boolean;
  colorOf: (task: TaskDTO) => ColorToken;
  selectedId: string | null;
  /** The slot the quick-create popover is open for, drawn as a placeholder entry. */
  draft: DraftSlot | null;
  /** What the quick-create popover's title field holds, shown on the placeholder. */
  draftTitle: string;
  draftRef: (element: HTMLElement | null) => void;
  onOpenEntry: (task: TaskDTO, anchor: HTMLElement) => void;
  onOpenDay: (day: Date) => void;
  onCreate: (slot: DraftSlot) => void;
  onReschedule: (task: TaskDTO, change: Reschedule) => void;
};

/** Day and week views: an all-day lane over a 24-hour grid of timed blocks. */
export function TimeGridView({
  days,
  entries,
  now,
  canEdit,
  colorOf,
  selectedId,
  draft,
  draftTitle,
  draftRef,
  onOpenEntry,
  onOpenDay,
  onCreate,
  onReschedule,
}: TimeGridViewProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);
  const laneRef = useRef<HTMLDivElement>(null);
  const suppressClick = useRef(false);
  const [drag, setDrag] = useState<GridDrag | null>(null);
  const begin = usePointerGesture();
  const columnCount = days.length;
  const template = { gridTemplateColumns: `${GUTTER} repeat(${columnCount}, minmax(0, 1fr))` };
  const todayIndex = days.findIndex((day) => isSameDay(day, now));

  // Open on the working day — or just before now when today is shown — instead of midnight. Mount only: moving
  // to another week keeps the hours the user scrolled to.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTop =
      todayIndex >= 0 ? Math.max(0, ((wallMinutes(now) - 60) / 60) * HOUR_HEIGHT) : 7 * HOUR_HEIGHT;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byId = useMemo(() => new Map(entries.map((entry) => [entry.task.id, entry])), [entries]);

  const lanePlacements = useMemo(
    () =>
      layoutMonthWeek(
        days,
        entries
          .filter((e) => e.timing.allDayLane)
          .map((e) => ({ id: e.task.id, start: e.timing.start, end: e.timing.end, bar: true })),
      ),
    [days, entries],
  );
  const lanesUsed = lanePlacements.reduce((max, p) => Math.max(max, p.lane + 1), 0);
  const laneDraft = draft?.allDay
    ? layoutMonthWeek(days, [{ id: 'draft', start: draft.start, end: draft.end, bar: true }])[0]
    : undefined;
  const laneSelecting = drag?.kind === 'lane-select';
  const laneRows = Math.max(1, lanesUsed + (laneDraft || laneSelecting ? 1 : 0));

  const columns = useMemo(() => {
    const perDay = days.map(() => [] as { entry: CalendarEntry; segment: DaySegment }[]);
    for (const entry of entries) {
      if (entry.timing.allDayLane) continue;
      for (const segment of daySegments(entry.timing, days))
        perDay[segment.dayIndex]?.push({ entry, segment });
    }
    return perDay.map((pieces) => ({
      pieces,
      layout: layoutTimedEntries(
        pieces.map((p) => ({
          id: p.entry.task.id,
          startMinutes: p.segment.startMinutes,
          endMinutes: p.segment.endMinutes,
        })),
      ),
    }));
  }, [days, entries]);

  // ---------------------------------------------------------------------------------------- geometry

  const pointToSlot = (point: Point): { col: number; minutes: number } => {
    const rect = columnsRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return { col: 0, minutes: 0 };
    const col = Math.min(
      columnCount - 1,
      Math.max(0, Math.floor(((point.x - rect.left) / rect.width) * columnCount)),
    );
    const minutes = Math.min(MINUTES_PER_DAY, Math.max(0, ((point.y - rect.top) / HOUR_HEIGHT) * 60));
    return { col, minutes };
  };

  const pointToLaneCol = (point: Point): number => {
    const rect = laneRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.min(
      columnCount - 1,
      Math.max(0, Math.floor(((point.x - rect.left) / rect.width) * columnCount)),
    );
  };

  /** Dragging near the top or bottom edge scrolls the hours. */
  const edgeScroll = (point: Point) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const top = headerRef.current?.getBoundingClientRect().bottom ?? scroller.getBoundingClientRect().top;
    const bottom = scroller.getBoundingClientRect().bottom;
    if (point.y < top + EDGE_SCROLL_PX) scroller.scrollTop -= 16;
    else if (point.y > bottom - EDGE_SCROLL_PX) scroller.scrollTop += 16;
  };

  const dayAt = (col: number): Date => days[col] ?? days[0] ?? now;

  /** A drag ends with the browser's click on whatever is under the pointer; that click must not open anything. */
  const swallowNextClick = () => {
    suppressClick.current = true;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 0);
  };

  const drop = (task: TaskDTO, change: Reschedule | null) => {
    swallowNextClick();
    setDrag(null);
    if (change && changesSchedule(task, change)) onReschedule(task, change);
  };

  // ---------------------------------------------------------------------------------------- gestures

  const onColumnPointerDown = (event: ReactPointerEvent, col: number) => {
    if (!canEdit || event.target !== event.currentTarget) return;
    const origin = pointToSlot({ x: event.clientX, y: event.clientY });
    begin(event, {
      onStart: () => setDrag({ kind: 'select', col, from: origin.minutes, to: origin.minutes }),
      onMove: (point) => {
        edgeScroll(point);
        setDrag({ kind: 'select', col, from: origin.minutes, to: pointToSlot(point).minutes });
      },
      onDrop: (point) => {
        swallowNextClick();
        setDrag(null);
        onCreate(rangeSlot(dayAt(col), origin.minutes, pointToSlot(point).minutes));
      },
      onCancel: () => setDrag(null),
      onTap: () => onCreate(slotAt(dayAt(col), origin.minutes)),
    });
  };

  const onEntryPointerDown = (event: ReactPointerEvent, task: TaskDTO) => {
    event.stopPropagation();
    if (!canEdit) return;
    const origin = pointToSlot({ x: event.clientX, y: event.clientY });
    const changeAt = (point: Point) => {
      const target = pointToSlot(point);
      return moveTimed(task, dayDiff(dayAt(origin.col), dayAt(target.col)), target.minutes - origin.minutes);
    };
    begin(event, {
      onStart: () => setDrag({ kind: 'move', task, change: null }),
      onMove: (point) => {
        edgeScroll(point);
        setDrag({ kind: 'move', task, change: changeAt(point) });
      },
      onDrop: (point) => drop(task, changeAt(point)),
      onCancel: () => setDrag(null),
    });
  };

  const onResizePointerDown = (event: ReactPointerEvent, task: TaskDTO) => {
    event.stopPropagation();
    const changeAt = (point: Point) => {
      const target = pointToSlot(point);
      return resizeTimed(task, dateAtMinutes(dayAt(target.col), snapMinutes(target.minutes)));
    };
    begin(event, {
      onStart: () => setDrag({ kind: 'resize', task, change: null }),
      onMove: (point) => {
        edgeScroll(point);
        setDrag({ kind: 'resize', task, change: changeAt(point) });
      },
      onDrop: (point) => drop(task, changeAt(point)),
      onCancel: () => setDrag(null),
    });
  };

  const onLanePointerDown = (event: ReactPointerEvent) => {
    if (!canEdit || event.target !== event.currentTarget) return;
    const from = pointToLaneCol({ x: event.clientX, y: event.clientY });
    begin(event, {
      onStart: () => setDrag({ kind: 'lane-select', from, to: from }),
      onMove: (point) => setDrag({ kind: 'lane-select', from, to: pointToLaneCol(point) }),
      onDrop: (point) => {
        swallowNextClick();
        setDrag(null);
        onCreate(allDaySlot(dayAt(from), dayAt(pointToLaneCol(point))));
      },
      onCancel: () => setDrag(null),
      onTap: () => onCreate(allDaySlot(dayAt(from), dayAt(from))),
    });
  };

  const onBarPointerDown = (event: ReactPointerEvent, task: TaskDTO) => {
    event.stopPropagation();
    if (!canEdit) return;
    const from = pointToLaneCol({ x: event.clientX, y: event.clientY });
    const changeAt = (point: Point) => moveByDays(task, dayDiff(dayAt(from), dayAt(pointToLaneCol(point))));
    begin(event, {
      onStart: () => setDrag({ kind: 'lane-move', task, change: null }),
      onMove: (point) => setDrag({ kind: 'lane-move', task, change: changeAt(point) }),
      onDrop: (point) => drop(task, changeAt(point)),
      onCancel: () => setDrag(null),
    });
  };

  const openEntry = (task: TaskDTO, element: HTMLElement) => {
    if (!suppressClick.current) onOpenEntry(task, element);
  };

  // ---------------------------------------------------------------------------------------- previews

  const dragged = drag && 'task' in drag ? drag : null;
  const preview = dragged?.change ? entryTiming({ ...dragged.task, ...dragged.change }) : null;
  const previewColor = dragged ? colorOf(dragged.task) : 'slate';
  const lanePreview =
    preview && dragged?.kind === 'lane-move'
      ? layoutMonthWeek(days, [{ id: 'preview', start: preview.start, end: preview.end, bar: true }])[0]
      : undefined;
  const draggedLane = dragged ? lanePlacements.find((p) => p.id === dragged.task.id)?.lane : undefined;
  const barStyle = (startCol: number, endCol: number, lane: number) => ({
    top: 2 + lane * LANE_HEIGHT,
    left: `calc(${(startCol / columnCount) * 100}% + 2px)`,
    width: `calc(${((endCol - startCol) / columnCount) * 100}% - 4px)`,
    height: LANE_HEIGHT - 3,
  });

  return (
    <div ref={scrollerRef} className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain select-none">
      {/* Day headers and the all-day lane stay on top while the hours scroll. */}
      <div ref={headerRef} className="sticky top-0 z-20 border-b border-slate-200 bg-surface">
        <div className="grid" style={template}>
          <div aria-hidden />
          {days.map((day) => {
            const today = isSameDay(day, now);
            return (
              <button
                key={day.getTime()}
                type="button"
                onClick={() => onOpenDay(day)}
                aria-label={FULL_DAY.format(day)}
                aria-current={today ? 'date' : undefined}
                className="group flex flex-col items-center gap-0.5 border-l border-slate-100 py-1.5 outline-none focus-visible:bg-slate-50"
              >
                <span
                  className={clsx(
                    'text-2xs font-semibold tracking-wide uppercase',
                    today ? 'text-ember-600' : 'text-slate-500',
                  )}
                >
                  {WEEKDAY.format(day)}
                </span>
                <span
                  className={clsx(
                    'flex size-8 items-center justify-center rounded-full text-lg leading-none tabular',
                    today ? 'bg-ember-500 text-white' : 'text-slate-800 group-hover:bg-slate-100',
                  )}
                >
                  {day.getDate()}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid border-t border-slate-100" style={template}>
          <div className="flex items-start justify-end pt-1 pr-2 text-2xs text-slate-500">All day</div>
          <div className="max-h-[5.75rem] overflow-y-auto" style={{ gridColumn: `span ${columnCount}` }}>
            <div
              ref={laneRef}
              role="presentation"
              onPointerDown={onLanePointerDown}
              className={clsx('relative', canEdit && 'cursor-cell')}
              style={{ height: laneRows * LANE_HEIGHT + 4 }}
            >
              {days.map((day, col) => (
                <div
                  key={day.getTime()}
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 border-l border-slate-100"
                  style={{ left: `${(col / columnCount) * 100}%` }}
                />
              ))}
              {lanePlacements.map((placement) => {
                const entry = byId.get(placement.id);
                if (!entry) return null;
                const { task } = entry;
                const moving = dragged?.task.id === task.id;
                return (
                  <button
                    key={task.id}
                    type="button"
                    aria-label={entryLabel(entry, now) + (isOverdue(task, now) ? ', overdue' : '')}
                    onPointerDown={(event) => onBarPointerDown(event, task)}
                    onClick={(event) => openEntry(task, event.currentTarget)}
                    className={clsx(
                      'absolute flex items-center gap-1 overflow-hidden rounded px-1.5 text-left text-xs',
                      entryFillClass(task, colorOf(task), selectedId === task.id),
                      placement.continuesBefore && 'rounded-l-none',
                      placement.continuesAfter && 'rounded-r-none',
                      canEdit && 'cursor-grab touch-manipulation',
                      moving && 'opacity-40',
                    )}
                    style={barStyle(placement.startCol, placement.endCol, placement.lane)}
                  >
                    {isOverdue(task, now) && <OverdueDot />}
                    {!task.allDay && (
                      <span className="shrink-0 opacity-80">{formatCompactTime(entry.timing.start)}</span>
                    )}
                    <span
                      className={clsx('truncate font-medium', task.status === 'completed' && 'line-through')}
                    >
                      {task.title}
                    </span>
                    <EntryIcons task={task} />
                  </button>
                );
              })}
              {lanePreview && (
                <div
                  aria-hidden
                  className={clsx(
                    'pointer-events-none absolute rounded border-l-[3px] opacity-90 shadow-popover',
                    calendarBlockClasses(previewColor),
                  )}
                  style={barStyle(lanePreview.startCol, lanePreview.endCol, draggedLane ?? 0)}
                />
              )}
              {drag?.kind === 'lane-select' && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute rounded bg-ember-100 ring-1 ring-ember-400"
                  style={barStyle(Math.min(drag.from, drag.to), Math.max(drag.from, drag.to) + 1, lanesUsed)}
                />
              )}
              {laneDraft && (
                <div
                  ref={draftRef}
                  className="absolute flex items-center rounded bg-ember-500 px-1.5 text-xs font-medium text-white shadow-popover"
                  style={barStyle(laneDraft.startCol, laneDraft.endCol, lanesUsed)}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <span className="truncate">{draftTitle.trim() || '(No title)'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid" style={template}>
        <div aria-hidden className="relative" style={{ height: DAY_HEIGHT }}>
          {HOURS.slice(1).map((hour) => (
            <span
              key={hour}
              className="absolute right-2 -translate-y-1/2 text-2xs whitespace-nowrap text-slate-500 tabular"
              style={{ top: hour * HOUR_HEIGHT }}
            >
              {formatCompactTime(new Date(2000, 0, 1, hour))}
            </span>
          ))}
        </div>

        <div
          ref={columnsRef}
          className="relative grid"
          style={{
            gridColumn: `span ${columnCount}`,
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
            height: DAY_HEIGHT,
          }}
        >
          <div aria-hidden className="pointer-events-none absolute inset-0">
            {HOURS.map((hour) => (
              <div key={hour} className="border-t border-slate-100" style={{ height: HOUR_HEIGHT }} />
            ))}
          </div>

          {days.map((day, col) => {
            const column = columns[col];
            const selection =
              drag?.kind === 'select' && drag.col === col ? rangeSlot(day, drag.from, drag.to) : null;
            const previewSegments =
              preview && dragged?.kind !== 'lane-move'
                ? daySegments(preview, days).filter((s) => s.dayIndex === col)
                : [];
            const draftSegments =
              draft && !draft.allDay ? daySegments({ ...draft, point: false, allDayLane: false }, days) : [];
            const draftSegment = draftSegments.find((s) => s.dayIndex === col);
            return (
              <div
                key={day.getTime()}
                role="presentation"
                onPointerDown={(event) => onColumnPointerDown(event, col)}
                className={clsx('relative border-l border-slate-100', canEdit && 'cursor-cell')}
              >
                {column?.pieces.map(({ entry, segment }) => {
                  const layout = column.layout.get(entry.task.id) ?? { column: 0, columns: 1, span: 1 };
                  const { task, timing } = entry;
                  const top = (segment.startMinutes / 60) * HOUR_HEIGHT;
                  const height = Math.min(
                    Math.max(((segment.endMinutes - segment.startMinutes) / 60) * HOUR_HEIGHT, MIN_BLOCK_PX),
                    DAY_HEIGHT - top,
                  );
                  const overdue = isOverdue(task, now);
                  const time = timing.point
                    ? formatTime(timing.start)
                    : formatTimeRange(timing.start, timing.end);
                  const resizable = canEdit && !segment.continuesAfter;
                  return (
                    <div
                      key={task.id}
                      className={clsx('absolute', dragged?.task.id === task.id && 'opacity-40')}
                      style={{
                        top,
                        height,
                        left: `calc(${(layout.column / layout.columns) * 100}% + 1px)`,
                        width: `calc(${(layout.span / layout.columns) * 100}% - 3px)`,
                      }}
                    >
                      <button
                        type="button"
                        aria-label={entryLabel(entry, now) + (overdue ? ', overdue' : '')}
                        onPointerDown={(event) => onEntryPointerDown(event, task)}
                        onClick={(event) => openEntry(task, event.currentTarget)}
                        className={clsx(
                          'flex h-full w-full flex-col overflow-hidden rounded-md px-1.5 text-left text-xs leading-tight shadow-xs',
                          height >= TWO_LINE_PX ? 'py-1' : 'justify-center',
                          entryFillClass(task, colorOf(task), selectedId === task.id),
                          segment.continuesBefore && 'rounded-t-none',
                          segment.continuesAfter && 'rounded-b-none',
                          canEdit && 'cursor-grab touch-manipulation',
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-1">
                          {overdue && <OverdueDot />}
                          <span
                            className={clsx(
                              'truncate font-medium',
                              task.status === 'completed' && 'line-through',
                            )}
                          >
                            {task.title}
                          </span>
                          {height < TWO_LINE_PX && (
                            <span className="shrink-0 opacity-80">{formatCompactTime(timing.start)}</span>
                          )}
                          <EntryIcons task={task} />
                        </span>
                        {height >= TWO_LINE_PX && (
                          <span className="truncate text-2xs opacity-80">{time}</span>
                        )}
                      </button>
                      {resizable && (
                        <div
                          aria-hidden
                          onPointerDown={(event) => onResizePointerDown(event, task)}
                          className="absolute inset-x-1 bottom-0 h-1.5 cursor-ns-resize touch-none"
                        />
                      )}
                    </div>
                  );
                })}

                {previewSegments.map((segment) => (
                  <div
                    key={`preview-${segment.dayIndex}`}
                    aria-hidden
                    className={clsx(
                      'pointer-events-none absolute inset-x-0.5 z-10 overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 text-xs font-medium shadow-popover',
                      calendarBlockClasses(previewColor),
                    )}
                    style={segmentStyle(segment)}
                  >
                    {dragged && preview && (
                      <>
                        <span className="block truncate">{dragged.task.title}</span>
                        <span className="block truncate text-2xs opacity-80">
                          {preview.point
                            ? formatTime(preview.start)
                            : formatTimeRange(preview.start, preview.end)}
                        </span>
                      </>
                    )}
                  </div>
                ))}

                {selection && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0.5 z-10 rounded-md bg-ember-100 px-1.5 py-1 text-xs text-ember-800 ring-1 ring-ember-400"
                    style={segmentStyle({
                      startMinutes: wallMinutes(selection.start),
                      endMinutes: minutesUntil(day, selection.end),
                    })}
                  >
                    {formatTimeRange(selection.start, selection.end)}
                  </div>
                )}

                {draft && draftSegment && (
                  <div
                    ref={draftSegment === draftSegments[0] ? draftRef : undefined}
                    onPointerDown={(event) => event.stopPropagation()}
                    className="absolute inset-x-0.5 z-10 overflow-hidden rounded-md bg-ember-500 px-1.5 py-1 text-xs text-white shadow-popover"
                    style={segmentStyle(draftSegment)}
                  >
                    <span className="block truncate font-medium">{draftTitle.trim() || '(No title)'}</span>
                    <span className="block truncate text-2xs opacity-90">
                      {formatTimeRange(draft.start, draft.end)}
                    </span>
                  </div>
                )}

                {col === todayIndex && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 z-10 h-0.5 bg-red-500"
                    style={{ top: (wallMinutes(now) / 60) * HOUR_HEIGHT - 1 }}
                  >
                    <span className="absolute -top-1 -left-1 size-2.5 rounded-full bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function segmentStyle(segment: Pick<DaySegment, 'startMinutes' | 'endMinutes'>) {
  const top = (segment.startMinutes / 60) * HOUR_HEIGHT;
  return {
    top,
    height: Math.min(
      Math.max(((segment.endMinutes - segment.startMinutes) / 60) * HOUR_HEIGHT, MIN_BLOCK_PX),
      DAY_HEIGHT - top,
    ),
  };
}

/** Clock minutes of `end` on `day`, where the next midnight is 1440. */
function minutesUntil(day: Date, end: Date): number {
  return isSameDay(day, end) ? wallMinutes(end) : MINUTES_PER_DAY;
}
